import React, { useState, useRef } from 'react';
import { useProjectConfig } from '../hooks/useProjectConfig';

interface UploadResponse {
  status: string;
  message: string;
  request_id: string;
  sns_message_id: string;
  position_title: string;
  sensitivity_designation: string;
}

interface GeneratedRecord {
  request_id: string;
  sns_message_id?: string;
  position_title: string;
  sensitivity_designation: string;
}

const PDTUpload: React.FC = () => {
  const projectConfig = useProjectConfig();
  const [activeTab, setActiveTab] = useState<'upload' | 'generate'>('upload');

  // Upload tab state
  const [file, setFile] = useState<File | null>(null);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState<UploadResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Generate tab state
  const [count, setCount] = useState(3);
  const [positionType, setPositionType] = useState('random');
  const [autoUpload, setAutoUpload] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [generatedRecords, setGeneratedRecords] = useState<GeneratedRecord[]>([]);
  const [generateError, setGenerateError] = useState<string | null>(null);

  const API_BASE_URL = 'https://a8jboiv7a5.execute-api.us-east-1.amazonaws.com';

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);

    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.type === 'application/json') {
      setFile(droppedFile);
      setError(null);
      setResult(null);
    } else {
      setError('Please drop a valid JSON file');
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setError(null);
      setResult(null);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file first');
      return;
    }

    setUploading(true);
    setError(null);
    setResult(null);

    try {
      // Read file as text
      const fileContent = await file.text();

      // Parse JSON
      let pdtData;
      try {
        pdtData = JSON.parse(fileContent);
      } catch (e) {
        throw new Error('Invalid JSON file. Please check the file format.');
      }

      // Validate required fields
      if (!pdtData.requestId) {
        throw new Error('PDT JSON must include a requestId field');
      }

      // Upload to API
      const response = await fetch(`${API_BASE_URL}/pdt/upload`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ pdt_data: pdtData }),
      });

      const responseData = await response.json();

      if (!response.ok) {
        throw new Error(responseData.error || 'Upload failed');
      }

      setResult(responseData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleReset = () => {
    setFile(null);
    setResult(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    setGenerateError(null);
    setGeneratedRecords([]);

    try {
      const response = await fetch(`${API_BASE_URL}/pdt/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          count,
          position_type: positionType,
          auto_upload: autoUpload,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Generation failed');
      }

      setGeneratedRecords(data.records || []);
    } catch (err) {
      setGenerateError(err instanceof Error ? err.message : 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="flex-1 bg-gray-900 text-gray-100 min-h-screen p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <header className="mb-8">
          <h1 className="text-3xl font-bold text-gray-100 mb-2">
            PDT Record Management
          </h1>
          <p className="text-gray-400">
            Upload files or generate realistic test data with AI
          </p>
        </header>

        {/* Tabs */}
        <div className="mb-6">
          <div className="border-b border-gray-700">
            <nav className="flex gap-8">
              <button
                onClick={() => setActiveTab('upload')}
                className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'upload'
                    ? 'border-blue-500 text-blue-500'
                    : 'border-transparent text-gray-400 hover:text-gray-300'
                }`}
              >
                File Upload
              </button>
              <button
                onClick={() => setActiveTab('generate')}
                className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors flex items-center gap-2 ${
                  activeTab === 'generate'
                    ? 'border-blue-500 text-blue-500'
                    : 'border-transparent text-gray-400 hover:text-gray-300'
                }`}
              >
                AI Generator
                <span className="bg-purple-600 text-xs px-2 py-0.5 rounded">NEW</span>
              </button>
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'upload' && (
          <>
            {/* Upload Area */}
            <div className="bg-gray-800 border border-gray-700 rounded-lg p-8 mb-6">
          <div
            className={`border-2 border-dashed rounded-lg p-12 text-center transition-colors ${
              dragging
                ? 'border-blue-500 bg-blue-900 bg-opacity-10'
                : 'border-gray-600 hover:border-gray-500'
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <svg
              className="mx-auto h-12 w-12 text-gray-400 mb-4"
              stroke="currentColor"
              fill="none"
              viewBox="0 0 48 48"
            >
              <path
                d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>

            {file ? (
              <div>
                <p className="text-lg text-gray-300 mb-2">Selected file:</p>
                <p className="text-blue-400 font-medium">{file.name}</p>
                <p className="text-sm text-gray-500 mt-1">
                  {(file.size / 1024).toFixed(2)} KB
                </p>
              </div>
            ) : (
              <div>
                <p className="text-lg text-gray-300 mb-2">
                  Drag and drop your PDT JSON file here
                </p>
                <p className="text-sm text-gray-500 mb-4">or</p>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors"
                >
                  Browse Files
                </button>
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept=".json,application/json"
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>

          {/* Action Buttons */}
          {file && !result && (
            <div className="mt-6 flex gap-4 justify-center">
              <button
                onClick={handleUpload}
                disabled={uploading}
                className="bg-green-600 hover:bg-green-700 disabled:bg-gray-600 text-white px-8 py-3 rounded-lg transition-colors font-medium flex items-center gap-2"
              >
                {uploading ? (
                  <>
                    <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></div>
                    Uploading...
                  </>
                ) : (
                  <>
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                      />
                    </svg>
                    Upload to Pipeline
                  </>
                )}
              </button>
              <button
                onClick={handleReset}
                className="bg-gray-700 hover:bg-gray-600 text-white px-6 py-3 rounded-lg transition-colors"
              >
                Cancel
              </button>
            </div>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-900 bg-opacity-30 border border-red-700 rounded-lg p-6 mb-6">
            <div className="flex items-start gap-3">
              <svg
                className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div>
                <h3 className="text-red-300 font-medium mb-1">Upload Failed</h3>
                <p className="text-red-200">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Success Message */}
        {result && (
          <div className="bg-green-900 bg-opacity-30 border border-green-700 rounded-lg p-6 mb-6">
            <div className="flex items-start gap-3">
              <svg
                className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div className="flex-1">
                <h3 className="text-green-300 font-medium mb-3">Upload Successful!</h3>
                <div className="bg-gray-800 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Request ID:</span>
                    <span className="text-gray-200 font-mono">{result.request_id}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Position:</span>
                    <span className="text-gray-200">{result.position_title}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Sensitivity:</span>
                    <span className="text-blue-400 font-medium">
                      {result.sensitivity_designation}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">SNS Message ID:</span>
                    <span className="text-gray-200 font-mono text-sm">
                      {result.sns_message_id.substring(0, 20)}...
                    </span>
                  </div>
                </div>
                <div className="mt-4 text-sm text-gray-400">
                  <p>
                    Your record has been published to SNS and will flow through the analytics
                    pipeline:
                  </p>
                  <div className="mt-2 flex items-center gap-2 text-xs">
                    <span className="text-green-400">SNS</span>
                    <span>→</span>
                    <span className="text-green-400">Firehose</span>
                    <span>→</span>
                    <span className="text-green-400">S3 Parquet</span>
                    <span>→</span>
                    <span className="text-green-400">Athena</span>
                    <span>→</span>
                    <span className="text-green-400">Dashboard</span>
                  </div>
                  <p className="mt-2">
                    Data will appear in the Executive Dashboard within ~60 seconds.
                  </p>
                </div>
                <div className="mt-4 flex gap-3">
                  <button
                    onClick={handleReset}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors text-sm"
                  >
                    Upload Another
                  </button>
                  <a
                    href="/executive-analytics"
                    className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-lg transition-colors text-sm"
                  >
                    View Dashboard
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Instructions */}
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-200 mb-4">How it Works</h2>
          <div className="space-y-3 text-sm text-gray-300">
            <div className="flex gap-3">
              <span className="text-blue-500 font-bold">1.</span>
              <p>
                Upload a PDT JSON file containing position designation data with a valid{' '}
                <code className="bg-gray-700 px-2 py-1 rounded">requestId</code>.
              </p>
            </div>
            <div className="flex gap-3">
              <span className="text-blue-500 font-bold">2.</span>
              <p>
                The system validates the JSON structure and publishes it to the SNS topic{' '}
                <code className="bg-gray-700 px-2 py-1 rounded">pdt-audit-payload</code>.
              </p>
            </div>
            <div className="flex gap-3">
              <span className="text-blue-500 font-bold">3.</span>
              <p>
                AWS Firehose transforms the JSON to Parquet format and stores it in S3 with
                proper partitioning.
              </p>
            </div>
            <div className="flex gap-3">
              <span className="text-blue-500 font-bold">4.</span>
              <p>
                The data becomes immediately queryable via AWS Athena and appears in the
                Executive Analytics Dashboard.
              </p>
            </div>
          </div>

          <div className="mt-6 bg-blue-900 bg-opacity-20 border border-blue-700 rounded-lg p-4">
            <p className="text-blue-300 text-sm">
              <strong>Live System:</strong> This is not a mock or simulation. Every upload
              goes through the actual AWS infrastructure and appears in real-time analytics.
            </p>
          </div>
        </div>
          </>
        )}

        {/* Generate Tab */}
        {activeTab === 'generate' && (
          <>
            {/* Generator Controls */}
            <div className="bg-gray-800 border border-gray-700 rounded-lg p-8 mb-6">
              <h2 className="text-xl font-semibold text-gray-100 mb-6 flex items-center gap-2">
                <svg className="w-6 h-6 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                AI-Powered PDT Generator
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                {/* Number of Records */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Number of Records
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="5"
                    value={count}
                    onChange={(e) => setCount(Math.max(1, Math.min(5, parseInt(e.target.value) || 1)))}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Max 5 (parallel generation)</p>
                </div>

                {/* Position Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Position Type
                  </label>
                  <select
                    value={positionType}
                    onChange={(e) => setPositionType(e.target.value)}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="random">Random Variety</option>
                    <option value="cybersecurity">Cybersecurity</option>
                    <option value="intelligence">Intelligence</option>
                    <option value="executive">Executive</option>
                    <option value="technical">Technical</option>
                    <option value="administrative">Administrative</option>
                  </select>
                </div>

                {/* Auto Upload Toggle */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Auto Upload to Pipeline
                  </label>
                  <button
                    onClick={() => setAutoUpload(!autoUpload)}
                    className={`w-full flex items-center justify-between px-4 py-2 rounded-lg border transition-colors ${
                      autoUpload
                        ? 'bg-green-600 border-green-500 text-white'
                        : 'bg-gray-700 border-gray-600 text-gray-300'
                    }`}
                  >
                    <span>{autoUpload ? 'Enabled' : 'Disabled'}</span>
                    <div className={`w-10 h-6 rounded-full relative transition-colors ${
                      autoUpload ? 'bg-green-400' : 'bg-gray-500'
                    }`}>
                      <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                        autoUpload ? 'translate-x-5' : 'translate-x-1'
                      }`}></div>
                    </div>
                  </button>
                </div>
              </div>

              {/* Generate Button */}
              <button
                onClick={handleGenerate}
                disabled={generating}
                className="w-full bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 text-white px-8 py-4 rounded-lg transition-colors font-medium flex items-center justify-center gap-3"
              >
                {generating ? (
                  <>
                    <div className="animate-spin h-6 w-6 border-3 border-white border-t-transparent rounded-full"></div>
                    Generating with AI...
                  </>
                ) : (
                  <>
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    Generate {count} Record{count > 1 ? 's' : ''} with AI
                  </>
                )}
              </button>

              <div className="mt-4 bg-purple-900 bg-opacity-20 border border-purple-700 rounded-lg p-4">
                <p className="text-purple-300 text-sm">
                  <strong>AI-Powered:</strong> Uses Claude to generate realistic PDT records with varied
                  positions, agencies, and designation outcomes. Records are generated in parallel (~10 seconds for 5 records).
                  {autoUpload && (
                    <span className="block mt-1">
                      <strong>Auto-Upload:</strong> Records will be automatically published to the live analytics pipeline
                      and visible in the dashboard within 60 seconds.
                    </span>
                  )}
                </p>
              </div>
            </div>

            {/* Generation Error */}
            {generateError && (
              <div className="bg-red-900 bg-opacity-30 border border-red-700 rounded-lg p-6 mb-6">
                <div className="flex items-start gap-3">
                  <svg className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <h3 className="text-red-300 font-medium mb-1">Generation Failed</h3>
                    <p className="text-red-200">{generateError}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Generated Records */}
            {generatedRecords.length > 0 && (
              <div className="bg-green-900 bg-opacity-30 border border-green-700 rounded-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  <svg className="w-6 h-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <h3 className="text-green-300 font-medium">
                    Generated {generatedRecords.length} Record{generatedRecords.length > 1 ? 's' : ''}
                    {autoUpload && ' and Uploaded to Pipeline'}
                  </h3>
                </div>

                <div className="space-y-3">
                  {generatedRecords.map((record, index) => (
                    <div key={index} className="bg-gray-800 rounded-lg p-4">
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <span className="text-gray-400">Request ID:</span>
                          <span className="ml-2 text-gray-200 font-mono">{record.request_id}</span>
                        </div>
                        <div>
                          <span className="text-gray-400">Position:</span>
                          <span className="ml-2 text-gray-200">{record.position_title}</span>
                        </div>
                        <div>
                          <span className="text-gray-400">Sensitivity:</span>
                          <span className="ml-2 text-blue-400 font-medium">{record.sensitivity_designation}</span>
                        </div>
                        {record.sns_message_id && (
                          <div>
                            <span className="text-gray-400">SNS Message:</span>
                            <span className="ml-2 text-gray-200 font-mono text-xs">
                              {record.sns_message_id.substring(0, 16)}...
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-4 text-sm text-gray-400">
                  <p>
                    {autoUpload
                      ? 'Records published to SNS and flowing through the analytics pipeline.'
                      : 'Records generated. Enable auto-upload to send to the live pipeline.'}
                  </p>
                  {autoUpload && (
                    <div className="mt-2 flex items-center gap-2 text-xs">
                      <span className="text-green-400">SNS</span>
                      <span>→</span>
                      <span className="text-green-400">Firehose</span>
                      <span>→</span>
                      <span className="text-green-400">S3 Parquet</span>
                      <span>→</span>
                      <span className="text-green-400">Athena</span>
                      <span>→</span>
                      <span className="text-green-400">Dashboard (60s)</span>
                    </div>
                  )}
                </div>

                <div className="mt-4 flex gap-3">
                  <button
                    onClick={handleGenerate}
                    className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg transition-colors text-sm"
                  >
                    Generate More
                  </button>
                  <a
                    href="/analytics"
                    className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-lg transition-colors text-sm"
                  >
                    View Dashboard
                  </a>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PDTUpload;
